import React, { useEffect, useRef, useState } from "react";
import CheckmarkIcon from "@src/lib/icons/18px/checkmark";
import SquaresCopyOutlineIcon from "@src/lib/icons/18px/squares-copy-outline";
import { Button } from "@src/lib/ui/Button";
import { cn } from "@src/lib/utils/cn";

const COPIED_RESET_MS = 1500;

// A fenced code block with a copy affordance in its top-right corner. The
// button reads the rendered text back from the DOM, so it copies exactly
// what the reader sees regardless of how the markdown nested its children.
export const MarkdownCodeBlock: React.FC<{
  className: string;
  children: React.ReactNode;
}> = ({ className, children }) => {
  const preRef = useRef<HTMLPreElement>(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timeout = setTimeout(() => setCopied(false), COPIED_RESET_MS);
    return () => clearTimeout(timeout);
  }, [copied]);
  const copy = async () => {
    const text = (preRef.current?.textContent ?? "").replace(/\n$/, "");
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className={cn(className, "group relative")}>
      <pre
        ref={preRef}
        className="overflow-x-auto rounded-lg bg-bg-secondary p-4 font-family-mono text-code-sm"
        data-testid="markdown-code-block"
      >
        {children}
      </pre>
      <Button
        type="button"
        variant="outline"
        size="xs"
        onClick={copy}
        aria-label={copied ? "Copied" : "Copy code"}
        data-testid="markdown-code-copy"
        className="absolute top-2 right-2 text-text-secondary opacity-0 group-hover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100"
      >
        {copied ? (
          <CheckmarkIcon data-icon="inline-start" />
        ) : (
          <SquaresCopyOutlineIcon data-icon="inline-start" />
        )}
        {copied ? "Copied" : "Copy"}
      </Button>
    </div>
  );
};
